import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Tabs, Tab, Grid, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Stack, IconButton, Button,
  Dialog, DialogTitle, DialogContent, Tooltip, InputAdornment,
} from "@mui/material"; // Removed unused imports
import { useAuth } from "../contexts/AuthContext"; // Import useAuth
import SearchIcon from "@mui/icons-material/Search"; // New import
import FileDownloadIcon from "@mui/icons-material/FileDownload"; // New import
import RefreshIcon from "@mui/icons-material/Refresh"; // New import
import { FormControl, InputLabel, Select, MenuItem, TextField } from "@mui/material"; // New imports
import { TablePagination } from "@mui/material"; // New import
import LoadingSpinner from "../components/LoadingSpinner"; // Assuming you have this
import Swal from "sweetalert2"; // For displaying errors
import { motion } from "framer-motion";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoIcon from "@mui/icons-material/Info";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import dayjs from "dayjs"; // Already imported
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import relativeTime from 'dayjs/plugin/relativeTime'; // For .fromNow()
// import {
//   fetchMemberEmergencyDeductions,
//   fetchMemberEmergencyInvoices,
// } from "../services/memberEmergencyService"; // New service imports
import { fetchMemberEmergencyInvoices, fetchEmergencyEvents as fetchMemberEmergencyDeductions } from "../services/emergencyService";
import { useTranslation } from "react-i18next";
import formatCurrency from "../shared/formatCurrency";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);

const brand = "#800b33";
const brandHover = "#6c0a2b";

const timeAgo = (dateString) => {
  return dayjs(dateString).fromNow();
};

export default function MemberEmergencyContributions() {
  const { memberId, loading: authLoading } = useAuth(); // Get memberId from context
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [deductions, setDeductions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true); // For data fetching
  const [error, setError] = useState(null);

  // Filter states
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all");
  const [deductionTypeFilter, setDeductionTypeFilter] = useState("all"); // New filter for deductions

  // Pagination states
  const [pageDeductions, setPageDeductions] = useState(0);
  const [rowsPerPageDeductions, setRowsPerPageDeductions] = useState(10);
  const [pageInvoices, setPageInvoices] = useState(0);
  const [rowsPerPageInvoices, setRowsPerPageInvoices] = useState(10);

  // Fetch data from API
  const loadData = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const [deductionsData, invoicesData] = await Promise.all([
        fetchMemberEmergencyDeductions(memberId),
        fetchMemberEmergencyInvoices(memberId),
      ]);
      setDeductions(deductionsData);
      setInvoices(invoicesData);
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to load data"),
        text: err.message,
        confirmButtonColor: brand,
      });
    } finally {
      setLoading(false);
    }
  }, [memberId, t]);

  const fadeIn = {
    hidden: { opacity: 0, y: 18 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.06, duration: 0.35, ease: "easeOut" },
    }),
  };

  useEffect(() => {
    if (!authLoading && memberId) {
      loadData();
    }
  }, [authLoading, memberId, loadData]);

  useEffect(() => {
    // No longer using static sample data here, useEffect above handles fetching
  }, []);

  // Utility for date formatting (consistent with TransactionsAudit.jsx)
  const formatDate = (d) => {
    return d ? dayjs(d).format("YYYY-MM-DD HH:mm") : "";
  };

  const statusChip = (status) =>
    status === "PAID"
      ? { label: t("PAID"), color: "success", icon: <DoneAllIcon fontSize="small" /> }
      : status === "PENDING"
        ? { label: t("PENDING"), color: "warning", icon: <PendingActionsIcon fontSize="small" /> }
        : { label: t(status), color: "default" };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    Swal.fire({
      icon: 'success',
      title: t("Copied!"),
      toast: true,
      position: 'bottom-end',
      showConfirmButton: false,
      timer: 1500
    });
  };

  // Reset filters function
  const resetFilters = () => {
    setQuery("");
    setFromDate("");
    setToDate("");
    setInvoiceStatusFilter("all");
    setDeductionTypeFilter("all");
    setPageDeductions(0);
    setPageInvoices(0);
  };

  // Filtered and paginated data for Deductions
  const filteredDeductions = useMemo(() => {
    // Helper to parse description for paidFromOverpayment and paidFromBalance
    const parseDeductionDetails = (description) => {
      const overpaymentMatch = description.match(/Paid from Overpayment: ([\d.]+)/);
      const balanceMatch = description.match(/from Main Balance: ([\d.]+)/);
      return {
        paidFromOverpayment: overpaymentMatch ? parseFloat(overpaymentMatch[1]) : 0,
        paidFromBalance: balanceMatch ? parseFloat(balanceMatch[1]) : 0,
      };
    };

    const q = query.trim().toLowerCase();
    return deductions.filter((d_api) => {
      const eventName = d_api.description.split('Deduction for emergency event: ')[1]?.split('. ')[0] || '';
      const eventNameMatch = eventName.toLowerCase().includes(q);
      const dateMatch =
        (!fromDate || dayjs(d_api.created_at).isSameOrAfter(dayjs(fromDate), 'day')) &&
        (!toDate || dayjs(d_api.created_at).isSameOrBefore(dayjs(toDate), 'day'));
      return eventNameMatch && dateMatch;
    });
  }, [deductions, query, fromDate, toDate]);

  const paginatedDeductions = useMemo(() => {
    const start = pageDeductions * rowsPerPageDeductions;
    const end = start + rowsPerPageDeductions;
    return rowsPerPageDeductions === -1 ? filteredDeductions : filteredDeductions.slice(start, end);
  }, [filteredDeductions, pageDeductions, rowsPerPageDeductions]);

  // Filtered and paginated data for Invoices
  const filteredInvoices = useMemo(() => {
    const q = query.trim().toLowerCase();
    return invoices.filter((inv_api) => {
      const eventNameMatch = inv_api.emergency_contribution.event_name.toLowerCase().includes(q);
      const referenceMatch = inv_api.invoice_id.toLowerCase().includes(q);
      const statusMatch = invoiceStatusFilter === "all" || inv_api.status === invoiceStatusFilter;
      const dateMatch =
        (!fromDate || dayjs(inv_api.created_at).isSameOrAfter(dayjs(fromDate), 'day')) &&
        (!toDate || dayjs(inv_api.created_at).isSameOrBefore(dayjs(toDate), 'day'));
      return (eventNameMatch || referenceMatch) && statusMatch && dateMatch;
    });
  }, [invoices, query, fromDate, toDate, invoiceStatusFilter]);

  const paginatedInvoices = useMemo(() => {
    const start = pageInvoices * rowsPerPageInvoices;
    const end = start + rowsPerPageInvoices;
    return rowsPerPageInvoices === -1 ? filteredInvoices : filteredInvoices.slice(start, end);
  }, [filteredInvoices, pageInvoices, rowsPerPageInvoices]);

  // CSV Export utility (copied from TransactionsAudit.jsx)
  const exportCSV = (filename, rows, columns) => {
    const header = columns.map((c) => `"${c.header}"`).join(",");
    const body = rows
      .map((r) =>
        columns
          .map((c) => {
            const v =
              typeof c.accessor === "function" ? c.accessor(r) : r[c.accessor];
            return `"${String(v ?? "").replace(/"/g, '""')}"`;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deductionColumns = [
    { header: "#", accessor: (r, idx) => idx + 1 }, // Index is handled by map
    { header: t("Event"), accessor: (r) => r.description.split('Deduction for emergency event: ')[1]?.split('. ')[0] || 'N/A' },
    { header: t("Deduction"), accessor: (r) => Math.abs(r.amount) },
    {
      header: t("From Overpayment"), accessor: (r) => {
        const match = r.description.match(/Paid from Overpayment: ([\d.]+)/);
        return match ? parseFloat(match[1]) : 0;
      }
    },
    {
      header: t("From Balance"), accessor: (r) => {
        const match = r.description.match(/from Main Balance: ([\d.]+)/);
        return match ? parseFloat(match[1]) : 0;
      }
    },
    { header: t("Date"), accessor: (r) => formatDate(r.created_at) },
  ];

  const invoiceColumns = [
    { header: "#", accessor: (r, idx) => idx + 1 }, // Index is handled by map
    { header: t("Event Name"), accessor: (r) => r.emergency_contribution.event_name },
    { header: t("Amount Due"), accessor: "amount_due" },
    { header: t("Reference"), accessor: "invoice_id" },
    { header: t("Status"), accessor: "status" },
    { header: t("Issued At"), accessor: (r) => formatDate(r.created_at) },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
        {t("My Emergency Contributions")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        {t("View all emergency deductions and pending invoices.")}
      </Typography>

      {(authLoading || loading) && <LoadingSpinner />}
      {error && !loading && (
        <Typography color="error" align="center" sx={{ my: 4 }}>
          {t("Error: {{message}}", { message: error })}
        </Typography>
      )}
      {/* Filters Card */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent>
          {/* ✅ TABS */}
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
            <Tab label={t("Emergency Deductions")} sx={{ fontWeight: 700 }} />
            <Tab label={t("Emergency Invoices")} sx={{ fontWeight: 700 }} />
          </Tabs>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t("Search event name or reference...")}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPageDeductions(0);
                  setPageInvoices(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Tab-specific filters */}
            {tab === 1 && ( // Only for Invoices tab
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("Status")}</InputLabel>
                  <Select
                    label={t("Status")}
                    value={invoiceStatusFilter}
                    onChange={(e) => {
                      setInvoiceStatusFilter(e.target.value);
                      setPageInvoices(0);
                    }}
                  >
                    <MenuItem value="all">{t("All")}</MenuItem>
                    <MenuItem value="PENDING">{t("PENDING")}</MenuItem>
                    <MenuItem value="PAID">{t("PAID")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {tab === 0 && ( // Only for Deductions tab
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t("Deduction Type")}</InputLabel>
                  <Select
                    label={t("Deduction Type")}
                    value={deductionTypeFilter}
                    onChange={(e) => {
                      setDeductionTypeFilter(e.target.value);
                      setPageDeductions(0);
                    }}
                  >
                    <MenuItem value="all">{t("All")}</MenuItem>
                    <MenuItem value="FROM_OVERPAYMENT">{t("From Overpayment")}</MenuItem>
                    <MenuItem value="FROM_BALANCE">{t("From Main Balance")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Date range filters */}
            <Grid item xs={6} md={tab === 1 ? 2.5 : 3.5}>
              <TextField
                label={t("From")}
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPageDeductions(0);
                  setPageInvoices(0);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={tab === 1 ? 2.5 : 3.5}>
              <TextField
                label={t("To")}
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPageDeductions(0);
                  setPageInvoices(0);
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Actions (Export CSV) */}
            <Grid item xs={12} md={tab === 1 ? 2 : 1} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-start" }, gap: 1 }}>
              <Tooltip title={t("Reset filters")}>
                <IconButton onClick={resetFilters}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

            </Grid>
          </Grid>
        </CardContent>
      </Card>



      {/* ✅ TAB 1 — EMERGENCY DEDUCTIONS */}
      {tab === 0 && (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t("Event")}</TableCell>
                      <TableCell>{t("Type")}</TableCell>
                      <TableCell>{t("Deduction")}</TableCell>
                      <TableCell>{t("From Overpayment")}</TableCell>
                      <TableCell>{t("From Balance")}</TableCell>
                      <TableCell>{t("Date")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDeductions.map((d, i) => (
                      <TableRow key={d.id} hover>
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>{d?.event_name}</TableCell>
                        <TableCell>{d.event_type}</TableCell>
                        <TableCell>{formatCurrency(Math.abs(d.amount_per_member))} TZS</TableCell>
                        <TableCell>{
                          formatCurrency(d.description.match(/Paid from Overpayment: ([\d.]+)/) ? parseFloat(d.description.match(/Paid from Overpayment: ([\d.]+)/)[1]) : 0)
                        } TZS</TableCell>
                        <TableCell>{
                          formatCurrency(d.description.match(/from Main Balance: ([\d.]+)/) ? parseFloat(d.description.match(/from Main Balance: ([\d.]+)/)[1]) : 0)
                        } TZS</TableCell>
                        <TableCell>{formatDate(d.created_at)}</TableCell>
                      </TableRow>
                    ))}
                    {paginatedDeductions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          {t("No emergency deductions found.")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredDeductions.length}
                page={pageDeductions}
                onPageChange={(_e, p) => setPageDeductions(p)}
                rowsPerPage={rowsPerPageDeductions}
                onRowsPerPageChange={(e) => {
                  setRowsPerPageDeductions(parseInt(e.target.value, 10));
                  setPageDeductions(0);
                }}
                rowsPerPageOptions={[5, 10, 25, { label: t("All"), value: -1 }]}
              />

            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* ✅ TAB 2 — EMERGENCY INVOICES */}
      {tab === 1 && (
        <motion.div variants={fadeIn} initial="hidden" animate="visible">
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow> {/* Added # column for consistency */}
                      <TableCell>#</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Event Name")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Event Deduction")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Your Debt")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("SSC Amount")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Total To Pay")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Reference")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t("Date")}</TableCell>


                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedInvoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          {t("No invoices.")}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedInvoices.map((inv, i) => {
                        return (
                          <TableRow key={inv.id} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{inv.emergency_contribution.event_name}</TableCell>
                            <TableCell>{formatCurrency(inv.emergency_contribution.amount_per_member)} TZS</TableCell>
                            <TableCell>{formatCurrency(inv.amount_due)} TZS</TableCell>
                            <TableCell>{formatCurrency(inv.df_amount)} TZS</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(inv.total_amount)} TZS</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {inv.invoice_id}
                                <IconButton size="small" onClick={() => handleCopy(inv.invoice_id)}>
                                  <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              {(() => {
                                const s = statusChip(inv.status);
                                return <Chip icon={s.icon} label={s.label} color={s.color} size="small" sx={{ fontWeight: 600 }} />;
                              })()}
                            </TableCell>

                            <TableCell>{formatDate(inv.created_at)}</TableCell>


                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>

                </Table>
                <TablePagination
                  component="div"
                  count={filteredInvoices.length}
                  page={pageInvoices}
                  onPageChange={(_e, p) => setPageInvoices(p)}
                  rowsPerPage={rowsPerPageInvoices}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPageInvoices(parseInt(e.target.value, 10));
                    setPageInvoices(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, { label: t("All"), value: -1 }]}
                />
              </TableContainer>
            </CardContent>
          </Card>
        </motion.div>
      )}

    </Box>
  );
}
